import React, { useEffect, useState } from "react";
import { ShieldCheck, Cpu, Loader2, RefreshCw } from "lucide-react";
import { adminAudit, adminAiSpend } from "@/lib/api";

/* Admin -> Audit & AI spend: who changed what (every admin mutation is logged
   automatically) and what the AI is costing, by feature and model. */
export default function AdminAudit() {
  const [audit, setAudit] = useState(null);
  const [spend, setSpend] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    Promise.all([
      adminAudit().then(({ data }) => setAudit(data.entries || [])).catch(() => setAudit([])),
      adminAiSpend().then(({ data }) => setSpend(data)).catch(() => setSpend(null)),
    ]).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const when = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const action = (path) => path.replace(/^\/api\/admin\/?/, "") || "root";
  const tone = (m) => m === "DELETE" ? "bg-red-900/40 text-red-300" : m === "POST" ? "bg-emerald-900/40 text-emerald-300" : "bg-blue-900/40 text-blue-300";

  return (
    <div data-testid="admin-audit">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-stone-100">Audit &amp; AI spend</h1>
          <p className="text-sm text-stone-400 mt-1">Every admin change is recorded automatically. AI cost is estimated per call from token usage.</p>
        </div>
        <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-700 text-stone-200 text-sm hover:bg-stone-800 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      <div className="mt-8 rounded-2xl border border-stone-800 bg-[#1c1917] p-6" data-testid="ai-spend-card">
        <h2 className="font-display text-xl text-stone-100 flex items-center gap-2"><Cpu size={18} className="text-[#C2410C]" /> AI spend</h2>
        {!spend ? <p className="text-sm text-stone-500 mt-3">No usage recorded yet.</p> : (
          <>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[["Today", spend.today], ["Last 7 days", spend.week], ["This month", spend.month]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-stone-800 bg-stone-900/40 px-4 py-3">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{l}</p>
                  <p className="font-display text-2xl text-stone-100 mt-1">{inr(v.inr)}</p>
                  <p className="text-xs text-stone-500">{v.calls} calls · {(v.tokens || 0).toLocaleString("en-IN")} tokens</p>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-5">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500 mb-2">By feature (this month)</p>
                {spend.by_feature.length === 0 ? <p className="text-xs text-stone-500">No calls yet</p> : spend.by_feature.map((r) => (
                  <div key={r.feature} className="flex items-center justify-between text-sm py-1.5 border-b border-stone-800/60">
                    <span className="text-stone-300">{r.feature}</span>
                    <span className="text-stone-400 font-mono text-xs">{r.calls} calls · <b className="text-stone-100">{inr(r.inr)}</b></span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500 mb-2">By model (this month)</p>
                {spend.by_model.length === 0 ? <p className="text-xs text-stone-500">No calls yet</p> : spend.by_model.map((r) => (
                  <div key={r.model} className="flex items-center justify-between text-sm py-1.5 border-b border-stone-800/60">
                    <span className="text-stone-300 font-mono text-xs">{r.model}</span>
                    <span className="text-stone-400 font-mono text-xs">{r.calls} calls · <b className="text-stone-100">{inr(r.inr)}</b></span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 rounded-2xl border border-stone-800 bg-[#1c1917] overflow-hidden" data-testid="audit-table">
        <div className="px-6 py-4 border-b border-stone-800 flex items-center gap-2">
          <ShieldCheck size={18} className="text-emerald-400" />
          <h2 className="font-display text-xl text-stone-100">Admin audit trail</h2>
          <span className="ml-auto text-xs text-stone-500">{audit ? `${audit.length} recent actions` : ""}</span>
        </div>
        {audit === null ? <p className="px-6 py-8 text-sm text-stone-500">Loading</p> : audit.length === 0 ? (
          <p className="px-6 py-8 text-sm text-stone-500">No admin changes recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-stone-300">
              <thead><tr className="border-b border-stone-800 text-[10px] font-mono uppercase tracking-widest text-stone-500">
                {["When", "Admin", "Action", "IP"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
              </tr></thead>
              <tbody>
                {audit.map((e, i) => (
                  <tr key={i} className="border-b border-stone-800/60 hover:bg-stone-900/40">
                    <td className="px-4 py-2.5 text-xs text-stone-400 whitespace-nowrap">{when(e.at)}</td>
                    <td className="px-4 py-2.5 text-xs">{e.admin_email}</td>
                    <td className="px-4 py-2.5 font-mono text-xs"><span className={`px-1.5 py-0.5 rounded mr-2 ${tone(e.method)}`}>{e.method}</span>{action(e.path)}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-500 font-mono">{e.ip || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
